import { isCloudinaryUrl } from '@storefronts/common-types'

type ImageType = 'thumbnail' | 'main' | 'lqip'

type Transformations = {
    thumbnail: string
    main: string
    lqip: string
}

const TRANSFORMATIONS: Transformations = {
    thumbnail: 'f_webp,fl_awebp,c_pad,b_auto,w_150,h_150,q_60',
    main: 'f_webp,fl_awebp,c_limit,w_1200,q_auto:good',
    // Tiny blurred preview used as a CSS background-image placeholder while
    // the full image loads. ~400 bytes on a slow connection vs ~80 KB for main.
    lqip: 'w_30,e_blur:1000,q_1,f_webp',
}

/** Pixel width Shopify serves for the PDP background placeholder (~few KB vs full-size). */
const SHOPIFY_LQIP_WIDTH_PX = 48

export const imageUrlTransform = (url: string, type: ImageType = 'main') => {
    if (!isCloudinaryUrl(url)) return url
    const [cloudinaryBase, baseUrl] = url.split('/upload/')
    return `${cloudinaryBase}/upload/${TRANSFORMATIONS[type]}/${baseUrl}`
}

/**
 * Shopify-hosted product/media URLs whose CDN honors `width` (see Shopify Liquid `image_url`).
 * Narrow on purpose — unknown hosts should fall back to shimmer, not speculative query params.
 */
export function isShopifyCdnResolvableImageUrl(url: string): boolean {
    if (!url) return false
    try {
        const { hostname, pathname } = new URL(url)
        if (
            hostname !== 'cdn.shopify.com' &&
            !hostname.endsWith('.myshopify.com')
        ) {
            return false
        }
        if (
            hostname === 'cdn.shopify.com' &&
            (pathname.startsWith('/s/files/') ||
                pathname.includes('/cdn/shop/'))
        ) {
            return true
        }
        if (hostname.endsWith('.myshopify.com')) {
            return pathname.startsWith('/cdn/shop/')
        }
        return false
    } catch {
        return false
    }
}

export function shopifyImageWithWidthPx(url: string, widthPx: number): string {
    try {
        const next = new URL(url)
        next.searchParams.set('width', String(widthPx))
        return next.href
    } catch {
        return url
    }
}

export function supportsImageLqipPreview(url: string): boolean {
    return isCloudinaryUrl(url) || isShopifyCdnResolvableImageUrl(url)
}

/** Tiny preview URL for carousel background placeholders (Cloudinary transforms or Shopify CDN width). */
export function imageLqipPreviewUrl(url: string): string {
    if (isCloudinaryUrl(url)) return imageUrlTransform(url, 'lqip')
    if (isShopifyCdnResolvableImageUrl(url)) {
        return shopifyImageWithWidthPx(url, SHOPIFY_LQIP_WIDTH_PX)
    }
    return url
}
